<div class="page-bg font-sans pb-10" id="dashboard-view-root">
  <div class="max-w-[1400px] mx-auto px-5 sm:px-7 lg:px-10 pt-5 space-y-4">

    <!-- Loading -->
    <div *ngIf="loading" class="flex items-center justify-center py-24">
      <div class="h-10 w-10 border-4 border-[#B01E3E]/20 border-t-[#B01E3E] rounded-full animate-spin"></div>
    </div>

    <ng-container *ngIf="!loading">

      <!-- ═══ HERO PANEL: Welcome + Stats merged ═══ -->
      <div class="dash-hero" id="dashboard-hero-section">
        <div class="flex flex-col lg:flex-row gap-5 lg:gap-8">

          <!-- Left: Greeting + CTA -->
          <div class="flex-1 min-w-0">
            <p class="text-[10px] font-semibold uppercase tracking-widest mb-0.5" style="color: #B01E3E;">
              Good day 👋
            </p>
            <h1 class="font-display font-bold text-xl sm:text-[22px] m-0 leading-snug text-gray-900">
              Welcome back to Weddingly
            </h1>
            <p class="text-sm text-gray-500 mt-1.5 mb-4">
              Based on your partner preferences, we found
              <strong class="text-gray-700">{{ getCompatibleMatchesCount() }} compatible matches</strong>
              waiting for you.
            </p>
            <div class="flex flex-wrap gap-2">
              <a routerLink="/profiles" class="btn-connect btn-ripple px-5 rounded-xl text-xs font-bold no-underline"
                style="flex: unset; padding: 7px 16px; border-radius: 10px;">
                <span class="material-symbols-outlined text-[15px]">search</span>
                View Matches
              </a>
              <a routerLink="/partner-preferences" *ngIf="hasPreferences"
                class="btn-view px-5 rounded-xl text-xs font-bold no-underline"
                style="flex: unset; padding: 9px 18px; border-radius: 12px;">
                <span class="material-symbols-outlined text-[16px]">tune</span>
                Edit Preferences
              </a>
              <a routerLink="/partner-preferences" *ngIf="!hasPreferences"
                class="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border border-amber-300 bg-amber-50 text-amber-700 no-underline transition-colors hover:bg-amber-100"
                style="flex: unset;">
                <span class="material-symbols-outlined text-[15px]">auto_awesome</span>
                Set Preferences
              </a>
            </div>
          </div>

          <!-- Right: Stats Grid (2x2) -->
          <div class="grid grid-cols-2 gap-2.5 lg:w-[340px] lg:flex-shrink-0" id="dashboard-stats-grid">

            <!-- Compatible Matches -->
            <div class="stat-card" (click)="hasPreferences ? resetToRecommended() : null">
              <div class="stat-icon-wrap" style="background: #FFF0F4;">
                <span class="material-symbols-outlined text-xl" style="color:#B01E3E;">visibility</span>
              </div>
              <div>
                <div class="stat-number">{{ profilesViewedToday }}</div>
                <div class="stat-label">Profiles Viewed Today</div>
              </div>
            </div>

            <!-- Interests Received -->
            <div class="stat-card" routerLink="/interests">
              <div class="stat-icon-wrap" style="background: #FFF7ED;">
                <span class="material-symbols-outlined text-xl fill-icon" style="color:#EA580C;">favorite</span>
              </div>
              <div>
                <div class="stat-number">{{ newRequests.length }}</div>
                <div class="stat-label">Interests</div>
              </div>
            </div>

            <!-- Accepted -->
            <div class="stat-card" routerLink="/interests" [queryParams]="{tab: 'accepted'}">
              <div class="stat-icon-wrap" style="background: #F0FDF4;">
                <span class="material-symbols-outlined text-xl fill-icon" style="color:#16A34A;">handshake</span>
              </div>
              <div>
                <div class="stat-number">{{ acceptedRequests.length }}</div>
                <div class="stat-label">Accepted</div>
              </div>
            </div>

            <!-- Favorites -->
            <div class="stat-card" routerLink="/favorites">
              <div class="stat-icon-wrap" style="background: #F5F3FF;">
                <span class="material-symbols-outlined text-xl fill-icon" style="color:#7C3AED;">star</span>
              </div>
              <div>
                <div class="stat-number">{{ likes }}</div>
                <div class="stat-label">Saved</div>
              </div>
            </div>

          </div>
        </div>

        <!-- ⭐ Profile Action Prompts integrated inside Welcome Hero Card -->
        <div *ngIf="loggedInProfile && (!hasUploadedHoroscope || !hasAddedGalleryPhotos)"
          class="pt-5 mt-5 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-3.5">

          <!-- Prompt 1: Upload Horoscope -->
          <div *ngIf="!hasUploadedHoroscope"
            class="bg-gradient-to-r from-amber-50/95 to-orange-50/80 border border-amber-300/80 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0 border border-amber-400/30 text-amber-700">
                <span class="material-symbols-outlined text-xl">auto_awesome</span>
              </div>
              <div>
                <div class="flex items-center gap-1.5">
                  <span class="font-bold text-sm text-amber-950">Upload Your Horoscope</span>
                  <span
                    class="px-1.5 py-0.5 rounded-full text-[9px] font-extrabold bg-amber-200/90 text-amber-900 uppercase">3x
                    More Responses</span>
                </div>
                <p class="text-[11px] text-amber-900/80 m-0 leading-snug mt-0.5">
                  Matches filter heavily by Raasi and Dosham. Get verified!
                </p>
              </div>
            </div>
            <a routerLink="/my-horoscope"
              class="shrink-0 px-3.5 py-2 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-white rounded-xl text-xs font-bold shadow-2xs flex items-center gap-1 no-underline transition-transform hover:scale-105 cursor-pointer">
              <span class="material-symbols-outlined text-[15px]">upload_file</span> Add Horoscope
            </a>
          </div>

          <!-- Prompt 2: Add Gallery Photos -->
          <div *ngIf="!hasAddedGalleryPhotos"
            class="bg-gradient-to-r from-rose-50/95 to-pink-50/80 border border-rose-300/80 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-rose-500/15 flex items-center justify-center shrink-0 border border-rose-400/30 text-[#B01E3E]">
                <span class="material-symbols-outlined text-xl">photo_camera</span>
              </div>
              <div>
                <div class="flex items-center gap-1.5">
                  <span class="font-bold text-sm text-rose-950">Add Gallery Photos</span>
                  <span
                    class="px-1.5 py-0.5 rounded-full text-[9px] font-extrabold bg-rose-200/90 text-[#B01E3E] uppercase">High
                    Impact</span>
                </div>
                <p class="text-[11px] text-rose-900/80 m-0 leading-snug mt-0.5">
                  Profiles with gallery photos receive 5x more views!
                </p>
              </div>
            </div>
            <a [routerLink]="['/my-profile']" [queryParams]="{tab: 'gallery'}"
              class="shrink-0 px-3.5 py-2 bg-gradient-to-r from-[#B01E3E] to-rose-700 hover:from-rose-900 hover:to-[#B01E3E] text-white rounded-xl text-xs font-bold shadow-2xs flex items-center gap-1 no-underline transition-transform hover:scale-105 cursor-pointer">
              <span class="material-symbols-outlined text-[15px]">add_photo_alternate</span> Upload Photos
            </a>
          </div>

        </div>
      </div>

      <!-- 👑 EXPRESS PERSONAL MATCH MANAGER BANNER -->
      <!-- <div *ngIf="currentSubscription?.current_plan === 'EXPRESS' || currentSubscription?.current_plan === 'PLATINUM' || loggedInProfile?.current_plan === 'EXPRESS' || loggedInProfile?.current_plan === 'PLATINUM'" class="my-6">
        <div class="bg-gradient-to-r from-rose-950 via-[#800020] to-slate-900 rounded-3xl p-6 sm:p-7 text-white shadow-xl border border-rose-800/60 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden group">
          <div class="absolute -right-10 -bottom-10 w-48 h-48 bg-amber-400/10 rounded-full blur-2xl pointer-events-none"></div>

          <div class="flex items-center gap-5 relative z-10">
            <div class="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-slate-950 flex items-center justify-center text-2xl sm:text-3xl font-black shadow-lg shrink-0 group-hover:scale-105 transition-transform">
              <i class="bi bi-patch-check-fill"></i>
            </div>
            <div>
              <div class="flex items-center gap-2 mb-1.5 flex-wrap">
                <span class="px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40 text-[10px] font-black uppercase tracking-wider">
                  Express VIP Matchmaking Suite
                </span>
                <span class="text-xs text-rose-200 font-medium hidden sm:inline">• Personal Matchmaker Assigned</span>
              </div>
              <h3 class="text-lg sm:text-xl font-display font-bold text-white m-0 flex items-center gap-2">
                Personal Match Manager Suite <span class="bg-gradient-to-r from-amber-200 to-amber-400 text-slate-950 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">PRO</span>
              </h3>
              <p class="text-xs text-slate-200 m-0 mt-1 max-w-xl leading-relaxed">
                Your dedicated Personal Match Manager has curated recommendations waiting for your review. Access background dossiers and chat directly in your executive suite.
              </p>
            </div>
          </div>

          <div class="flex items-center shrink-0 relative z-10 w-full md:w-auto">
            <a routerLink="/express-concierge" class="w-full sm:w-auto px-6 py-3.5 rounded-2xl bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 hover:from-amber-300 hover:to-yellow-300 text-slate-950 text-xs font-black tracking-wide transition-all shadow-lg flex items-center justify-center gap-2.5 no-underline group-hover:shadow-amber-400/30">
              <i class="bi bi-stars text-sm text-slate-950"></i> Open Personal Match Manager
            </a>
          </div>
        </div>
      </div> -->

      <!-- 👑 PREMIUM MEMBERSHIP & QUOTA MANAGER -->
      <div *ngIf="currentSubscription"
        class="bg-gradient-to-r from-rose-950 via-[#800020] to-rose-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl border border-rose-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden my-4"
        id="premium-membership-section">
        <div class="absolute -right-10 -top-10 w-48 h-48 bg-white/5 rounded-full blur-2xl pointer-events-none"></div>

        <div class="space-y-2 max-w-xl z-10">
          <div class="flex items-center gap-2.5">
            <span
              class="px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-widest bg-amber-400 text-amber-950 shadow-md flex items-center gap-1">
              <span class="material-symbols-outlined text-[14px] fill-icon">workspace_premium</span>
              {{ currentSubscription.current_plan === 'FREE' ? 'Standard Plan' : 'Premium Member' }}
            </span>
            <span *ngIf="currentSubscription.plan_expiry" class="text-xs text-rose-200">
              Expires: {{ currentSubscription.plan_expiry | date:'mediumDate' }}
            </span>
          </div>
          <h2 class="font-display font-bold text-xl sm:text-2xl m-0 text-white tracking-tight">
            {{ currentSubscription.current_plan === 'FREE' ? 'Upgrade to enjoy more benefits' : 'Active Membership
            Benefits' }}
          </h2>
          <p class="text-xs sm:text-sm text-rose-100/80 m-0 leading-relaxed">
            {{ currentSubscription.current_plan === 'FREE' ? 'Unlock verified contact details, unlimited discovery, and
            Profile Boost to rise above the competition.' : 'You have active member privileges, full photo gallery
            access, and instant priority messaging.' }}
          </p>

          <div *ngIf="currentSubscription.usage" class="flex flex-wrap items-center gap-4 pt-2">
            <div class="bg-black/20 backdrop-blur px-3 py-1.5 rounded-xl border border-white/10 text-xs">
              👀 Views Today: <b>{{ currentSubscription.usage.profile_views_today }}</b> / {{
              currentSubscription.features?.dailyProfileLimit > 1000 ? '∞' :
              currentSubscription.features?.dailyProfileLimit }}
            </div>
            <div class="bg-black/20 backdrop-blur px-3 py-1.5 rounded-xl border border-white/10 text-xs">
              💌 Interests This Month: <b>{{ currentSubscription.usage.interests_sent_this_month }}</b> / {{
              currentSubscription.features?.monthlyInterestLimit > 1000 ? '∞' :
              currentSubscription.features?.monthlyInterestLimit }}
            </div>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto z-10">

          <!-- ═══ BOOST WIDGET — 4 states ═══ -->
          <ng-container *ngIf="boostStatus">

            <!-- STATE 1: Boost Active — live countdown -->
            <div *ngIf="boostStatus.boostActive"
              class="flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-emerald-600/90 border border-emerald-400/50 shadow-lg text-white min-w-[150px] text-center">
              <div class="flex items-center gap-1.5 font-extrabold text-xs uppercase tracking-wide">
                <span class="material-symbols-outlined text-[15px] animate-pulse">rocket_launch</span>
                Boost Active
              </div>
              <div class="font-mono text-lg font-black tracking-widest text-emerald-100">{{ boostCountdownLabel }}</div>
              <div class="text-[10px] text-emerald-200 font-medium">remaining · {{ getBoostsRemaining() }} boost{{
                getBoostsRemaining() !== 1 ? 's' : '' }} left this month</div>
            </div>

            <!-- STATE 2: Cooldown Active — show next available date -->
            <div *ngIf="!boostStatus.boostActive && boostStatus.cooldownActive"
              class="flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-black/25 border border-amber-300/20 shadow-lg text-white min-w-[150px] text-center opacity-80">
              <div class="flex items-center gap-1.5 font-extrabold text-xs uppercase tracking-wide text-amber-200">
                <span class="material-symbols-outlined text-[15px]">hourglass_empty</span>
                On Cooldown
              </div>
              <div class="text-xs font-semibold text-white/90">{{ getCooldownLabel() }}</div>
              <div class="text-[10px] text-rose-200 font-medium">{{ getBoostsRemaining() }} boost{{ getBoostsRemaining()
                !== 1 ? 's' : '' }} left this month</div>
            </div>

            <!-- STATE 3: Quota Exceeded -->
            <div *ngIf="!boostStatus.boostActive && !boostStatus.cooldownActive && boostStatus.quotaExceeded"
              class="flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-black/25 border border-amber-300/20 shadow-lg text-white min-w-[150px] text-center opacity-80">
              <div class="flex items-center gap-1.5 font-extrabold text-xs uppercase tracking-wide text-amber-200">
                <span class="material-symbols-outlined text-[15px]">block</span>
                Monthly Limit
              </div>
              <div class="text-[10px] text-rose-200 font-medium">All {{ boostStatus.boostMonthlyLimit }} boosts used
              </div>
              <div class="text-[10px] text-rose-300/80">Resets 1st of next month</div>
            </div>

            <!-- STATE 4: Available Now — can boost -->
            <button *ngIf="boostStatus.canBoostNow && !boostStatus.boostActive" (click)="triggerProfileBoost()"
              [disabled]="boostingInProgress"
              class="flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 border border-amber-300/40 shadow-lg text-white cursor-pointer transition-all hover:scale-105 active:scale-95 min-w-[150px] text-center">
              <div class="flex items-center gap-1.5 font-extrabold text-xs uppercase tracking-wide">
                <span class="material-symbols-outlined text-[15px]" [class.animate-spin]="boostingInProgress">{{
                  boostingInProgress ? 'autorenew' : 'bolt' }}</span>
                {{ boostingInProgress ? 'Activating...' : '24h Profile Boost' }}
              </div>
              <div class="text-[10px] text-amber-100 font-medium">{{ getBoostsRemaining() }} of {{
                boostStatus.boostMonthlyLimit }} remaining</div>
            </button>

          </ng-container>

          <!-- STATE 0: Locked Boost — FREE & SILVER users -->
          <a *ngIf="!boostStatus || !boostStatus.hasBoostFeature" routerLink="/plans"
            class="flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-black/35 border border-amber-400/40 hover:border-amber-400/80 shadow-lg text-white no-underline cursor-pointer transition-all hover:scale-105 active:scale-95 min-w-[150px] text-center group relative overflow-hidden">
            <div
              class="absolute -right-6 -top-6 w-16 h-16 bg-amber-400/10 rounded-full blur-xl pointer-events-none group-hover:bg-amber-400/20">
            </div>
            <div
              class="flex items-center gap-1.5 font-extrabold text-xs uppercase tracking-wide text-amber-300 group-hover:text-amber-200">
              <span class="material-symbols-outlined text-[15px] text-amber-400">lock</span>
              24h Profile Boost
            </div>
            <div class="text-[10px] text-amber-100/90 font-medium">Upgrade to Gold to Boost</div>
            <div class="text-[9px] text-rose-200/80">Get 4x top visibility</div>
          </a>

          <a routerLink="/plans"
            class="px-6 py-3 rounded-2xl bg-black/20 text-white border border-amber-300/30 font-bold text-xs shadow-lg transition-all hover:bg-black/30 hover:scale-105 active:scale-95 no-underline flex items-center justify-center gap-1.5 text-center cursor-pointer">
            <span class="material-symbols-outlined text-base fill-icon text-amber-300">stars</span>
            {{ currentSubscription.current_plan === 'FREE' ? 'Upgrade for More Benefits' : 'Explore Membership' }}
          </a>
        </div>
      </div>

      <!-- ═══ RECOMMENDED MATCHES SECTION ═══ -->
      <div class="px-2 sm:px-4 lg:px-6">
        <!-- Section Header -->
        <div class="section-header">
          <div>
            <h3>{{ isBasicSearchActive ? 'Search Results' : 'Recommended Matches' }}</h3>
            <p>
              {{ isBasicSearchActive ? 'Profiles matching your filters' : 'Based on your partner preferences' }}
              &nbsp;·&nbsp;
              <strong class="text-gray-700">{{ displayMatches.length }} Profiles</strong>
            </p>
          </div>
          <a routerLink="/profiles"
            class="text-xs font-bold no-underline flex items-center gap-1 transition-colors hover:opacity-80"
            style="color: #B01E3E;">
            View All
            <span class="material-symbols-outlined text-[16px]">arrow_forward</span>
          </a>
        </div>

        <!-- Profile Card Grid -->
        <div id="profile-feed-container">
          <div *ngIf="paginatedMatches.length > 0; else emptyState" class="profile-grid">

            <div *ngFor="let profile of paginatedMatches" class="pcard"
              [ngClass]="{'featured-vip-card': isFeaturedProfile(profile)}"
              [routerLink]="['/profile-details', profile._id]">

              <!-- Image -->
              <div class="pcard-img">
                <!-- ⭐ Refined Executive VIP Badge (top-right) -->
                <div *ngIf="isFeaturedProfile(profile)" class="vip-pill-badge">
                  <span class="material-symbols-outlined">verified</span> VIP GOLD
                </div>

                <ng-container *ngIf="base64Images[profile._id]; else defaultAvatar">
                  <img [src]="'data:image/jpeg;base64,' + base64Images[profile._id]"
                    alt="{{ profile.basic_info?.first_name }}'s photo" />
                </ng-container>
                <ng-template #defaultAvatar>
                  <div class="pcard-avatar-placeholder">
                    <span class="material-symbols-outlined text-[72px]"
                      style="color: rgba(176,30,62,0.15);">person</span>
                  </div>
                </ng-template>

                <!-- Gradient overlay -->
                <div class="pcard-gradient"></div>

                <!-- Match Badge top-left -->
                <div *ngIf="profile.match_badge && !isBasicSearchActive" class="pcard-match-badge" [ngClass]="{
                    'badge-strong':  profile.match_badge === 'Strong Match' || profile.match_badge === 'Preference Match',
                    'badge-partial': profile.match_badge === 'Partial Match',
                    'badge-similar': profile.match_badge === 'Similar Match',
                    'badge-discover': profile.match_badge === 'Discover'
                  }">
                  <span class="material-symbols-outlined text-[11px]">
                    {{ profile.match_badge === 'Preference Match' ? 'favorite' :
                    profile.match_badge === 'Strong Match' ? 'favorite' :
                    profile.match_badge === 'Partial Match' ? 'thumb_up' :
                    profile.match_badge === 'Similar Match' ? 'person_check' : 'explore' }}
                  </span>
                  {{ profile.match_badge }}
                </div>

                <!-- Favorite Button -->
                <button (click)="$event.stopPropagation(); toggleFavorite(profile)" class="pcard-fav-btn"
                  [class.is-favorited]="isFavorited(profile._id)">
                  <span class="material-symbols-outlined text-[15px]">favorite</span>
                </button>

                <!-- Name & Metadata Overlay -->
                <div class="pcard-overlay">
                  <div class="flex items-center gap-1.5 mb-0.5">
                    <h3 class="font-display text-[15px] font-bold m-0 truncate text-white drop-shadow-sm leading-tight">
                      {{ profile.basic_info?.first_name }} {{ profile.basic_info?.last_name }}
                    </h3>
                    <span *ngIf="profile.verification_status === 'VERIFIED'"
                      class="material-symbols-outlined fill-icon text-[14px] flex-shrink-0 drop-shadow text-white"
                      title="Verified Member">verified</span>
                  </div>
                  <div class="flex items-center gap-1.5 text-white/85 text-[11px] font-normal drop-shadow-sm truncate">
                    <span>{{ getAge(profile.basic_info?.date_of_birth) ? getAge(profile.basic_info?.date_of_birth) + '
                      yrs' : 'Age N/A' }}</span>
                    <span class="opacity-40">·</span>
                    <span class="truncate">{{ getCityName(profile.residing_location?.city_id) || 'Location N/A'
                      }}</span>
                    <ng-container *ngIf="profile.horoscope_file_url">
                      <span class="opacity-40">·</span>
                      <span class="flex items-center gap-0.5 text-white flex-shrink-0 font-medium"
                        title="Horoscope Available">
                        <span class="material-symbols-outlined text-[11px]">auto_awesome</span>Horoscope
                      </span>
                    </ng-container>
                  </div>
                  <div *ngIf="profile.preference_fit !== undefined && !isBasicSearchActive"
                    class="flex items-center gap-1 mt-1 text-[11px] font-semibold text-white drop-shadow-sm">
                    <span class="material-symbols-outlined text-[12px] text-white fill-icon">favorite</span>
                    <span>{{ profile.preference_fit }}% Compatible</span>
                  </div>
                </div>
              </div>

              <!-- Info Chips -->
              <div class="info-chips">
                <span class="info-chip" title="Religion">
                  <span class="material-symbols-outlined text-[12px]">temple_hindu</span>
                  {{ getReligionName(profile.religion_info?.religion_id) || 'Religion N/A' }}
                </span>
                <span class="info-chip" title="Education">
                  <span class="material-symbols-outlined text-[12px]">school</span>
                  {{ getEducationName(profile.professional_info?.education_id) || 'Education N/A' }}
                </span>
                <span class="info-chip" title="Diet">
                  <span class="material-symbols-outlined text-[12px]">restaurant</span>
                  {{ profile.personal_info?.food || 'Diet N/A' }}
                </span>
                <span *ngIf="getOccupationName(profile.professional_info?.occupation_id) !== 'Professional'"
                  class="info-chip" title="Occupation">
                  <span class="material-symbols-outlined text-[12px]">work</span>
                  {{ getOccupationName(profile.professional_info?.occupation_id) || 'Occupation N/A' }}
                </span>
              </div>

              <!-- Action Buttons -->
              <div class="pcard-actions">
                <a [routerLink]="['/profile-details', profile._id]" (click)="$event.stopPropagation()"
                  class="btn-view">View</a>

                <ng-container [ngSwitch]="getSentInterestStatus(profile)">
                  <button *ngSwitchCase="'Pending'" (click)="$event.stopPropagation(); cancelInterest(profile)"
                    title="Click to cancel interest request"
                    class="btn-pending group relative cursor-pointer hover:bg-red-50 hover:border-red-200 transition-all">
                    <span class="flex items-center gap-1 group-hover:hidden">
                      <span class="material-symbols-outlined text-[14px]">schedule</span>
                      Awaiting
                    </span>
                    <span class="hidden group-hover:inline-flex items-center gap-1 text-red-600 font-bold">
                      <span class="material-symbols-outlined text-[14px]">close</span> Cancel Req
                    </span>
                  </button>

                  <a *ngSwitchCase="'Accepted'" [routerLink]="['/chat']" (click)="$event.stopPropagation()"
                    class="btn-chat">
                    <span class="material-symbols-outlined text-[14px]">chat</span>
                    Chat
                  </a>

                  <button *ngSwitchCase="'Denied'" (click)="$event.stopPropagation()" class="btn-declined">
                    <span class="material-symbols-outlined text-[14px]">block</span>
                    Declined
                  </button>

                  <button *ngSwitchDefault (click)="$event.stopPropagation(); triggerInterestAlert(profile)"
                    class="btn-connect btn-ripple">
                    <span class="material-symbols-outlined text-[15px] fill-icon">send</span>
                    Connect
                  </button>
                </ng-container>
              </div>
            </div>

          </div>

          <!-- Empty State -->
          <ng-template #emptyState>
            <div
              class="bg-white rounded-[20px] p-14 text-center border border-[#EBEBEB] shadow-sm max-w-sm mx-auto mt-6"
              id="empty-state-card">
              <div class="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style="background: #FFF0F4;">
                <span class="material-symbols-outlined text-3xl" style="color:#B01E3E;">search_off</span>
              </div>
              <h3 class="font-display font-bold text-lg text-gray-800 m-0">No Matches Found</h3>
              <p class="text-gray-400 text-xs leading-relaxed mt-2 mb-5">
                Try relaxing your partner preferences to see more profiles.
              </p>
              <button id="reset-filters-btn" (click)="resetAllFilters()"
                class="btn-connect btn-ripple px-6 rounded-xl text-xs font-bold border-0 cursor-pointer"
                style="flex: unset; padding: 10px 22px; border-radius: 12px;">
                Reset Filters
              </button>
            </div>
          </ng-template>

          <!-- Pagination -->
          <div *ngIf="displayMatches.length > 10" class="flex justify-center gap-3 mt-8">
            <button *ngIf="currentPage > 1" (click)="showLess()"
              class="px-5 py-2 bg-white border border-[#EBEBEB] text-gray-500 text-xs font-semibold rounded-full shadow-sm hover:bg-gray-50 transition-all flex items-center gap-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-[14px]">expand_less</span>
              Show Less
            </button>
            <button *ngIf="paginatedMatches.length < displayMatches.length" (click)="loadMore()"
              class="px-5 py-2 border text-xs font-semibold rounded-full shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
              style="background: #FFF0F4; color: #B01E3E; border-color: rgba(176,30,62,0.25);">
              <span class="material-symbols-outlined text-[14px]">expand_more</span>
              Show More ({{ displayMatches.length - paginatedMatches.length }} remaining)
            </button>
          </div>

        </div>
      </div>

    </ng-container>
  </div>
</div>